import api from '../client'

const scopedConfig = (config = {}, overrides = {}) => ({
  ...config,
  ...overrides,
  _skipOrgRewrite: true,
})

// A lineup read waits at most this long (#1142). The route's own budget is
// 10 s (InteractiveRead), so a read still open after it is stalled, and the
// Surfaces page would otherwise wait on it forever.
export const LINEUP_READ_TIMEOUT_MS = 15_000

export const prepacksApi = {
  quote: async (orgSlug, payload, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/quote`, payload, scopedConfig(config))
  ).data,
  quoteTargets: async (orgSlug, payload, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/quote-targets`, payload, scopedConfig(config))
  ).data,
  analyzePrepack: async (orgSlug, id, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}/analyze`,
      undefined,
      scopedConfig(config),
    )
    return response.data
  },
  // A new title for a saved pack, keeping its picks (#984): payload may carry
  // title_style, direction and the updated_at the drawer read.
  regenerateTitle: async (orgSlug, id, payload = {}, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}/title`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },
  getRegulations: async (orgSlug, config = {}) => {
    const response = await api.get(`/${orgSlug}/prepacks/regulations`, scopedConfig(config))
    return response.data
  },
  // Every published pack assigned to one surface. Surface ranking and locked
  // positions need the complete assignment, which the surface bounds; screens
  // that browse the library page it with listPrepacks instead.
  listSurfacePrepacks: async (orgSlug, surfaceSlug, config = {}) => {
    const items = []
    let total = 0
    do {
      const response = await api.get(
        `/${orgSlug}/prepacks/library`,
        scopedConfig(config, { params: { status: 'published', surface: surfaceSlug, sort: 'updated', limit: 500, offset: items.length } }),
      )
      const page = response.data
      items.push(...page.items)
      total = page.total
      if (!page.items.length) break
    } while (items.length < total)
    return { items, total }
  },
  // Library
  listPrepacks: async (orgSlug, params = {}, config = {}) => {
    const response = await api.get(`/${orgSlug}/prepacks/library`, scopedConfig(config, { params }))
    return response.data
  },
  getLibraryFacets: async (orgSlug, config = {}) => {
    const response = await api.get(`/${orgSlug}/prepacks/library/facets`, scopedConfig(config))
    return response.data
  },
  getPrepack: async (orgSlug, id, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}`,
      scopedConfig(config),
    )
    return response.data
  },
  // The schedule a pack's legs make, live or expired since it was copied:
  // `{ prepack_id, schedulable, reason, message, recipe_id, recipe, template_id, regulation }`.
  // Nothing is saved.
  getPackScheduleDraft: async (orgSlug, id, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}/schedule-draft`,
      scopedConfig(config),
    )
    return response.data
  },
  createPrepack: async (orgSlug, payload, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/library`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },
  updatePrepack: async (orgSlug, id, payload, config = {}) => {
    const response = await api.put(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },
  // Remove packs (#1090): the pack's schedule, its packs in the pack's
  // regulation and the surfaces they are on.
  getRemovalPreview: async (orgSlug, id, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}/removal-preview`,
      scopedConfig(config),
    )
    return response.data
  },
  // payload: { scope: 'pack' | 'schedule', surfaces, stop_future, pack_updated_at? }
  removePlacements: async (orgSlug, id, payload, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}/removals`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },
  undoPlacementRemoval: async (orgSlug, removalId, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/removals/${encodeURIComponent(removalId)}/undo`,
      {},
      scopedConfig(config),
    )
    return response.data
  },
  // Recent removals (#1090): the organization's Remove packs actions still
  // undoable, or undone, within the hour; surface narrows to those that named it.
  listPlacementRemovals: async (orgSlug, { surface } = {}, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/removals`,
      scopedConfig(config, { params: surface ? { surface } : {} }),
    )
    return response.data
  },
  deletePrepack: async (orgSlug, id, config = {}) => {
    const response = await api.delete(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}`,
      scopedConfig(config),
    )
    return response.data
  },
  publishPrepack: async (orgSlug, id, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}/publish`,
      undefined,
      scopedConfig(config),
    )
    return response.data
  },
  unpublishPrepack: async (orgSlug, id, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}/unpublish`,
      undefined,
      scopedConfig(config),
    )
    return response.data
  },
  // Gives fields an operator changed on a pack back to its schedule, or its
  // look back to its template (#1089): `{ fields, updated_at }`.
  resetPrepack: async (orgSlug, id, payload, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/library/${encodeURIComponent(id)}/reset`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },
  // A save's update of the packs it made before (#1089): `{ id, status,
  // counts, items, refill, ... }`, read until its status is done or failed.
  getPropagation: async (orgSlug, id, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/propagations/${encodeURIComponent(id)}`,
      scopedConfig(config),
    )
    return response.data
  },
  // Queues a finished update again and answers with the queued one.
  retryPropagation: async (orgSlug, id, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/propagations/${encodeURIComponent(id)}/retry`,
      undefined,
      scopedConfig(config),
    )
    return response.data
  },
  // How many live packs saving the definition would take off surfaces:
  // `{ live_removed }`. Nothing is saved.
  scheduleImpact: async (orgSlug, recipeId, definition, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/recipes/${encodeURIComponent(recipeId)}/impact`,
      { definition },
      scopedConfig(config),
    )
    return response.data
  },

  // Surfaces
  listSurfaces: async (orgSlug, config = {}) => {
    const response = await api.get(`/${orgSlug}/prepacks/surfaces`, scopedConfig(config))
    return response.data
  },
  getSurface: async (orgSlug, slug, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}`,
      scopedConfig(config),
    )
    return response.data
  },
  // Surface lineups (#990). `view` is the page asked for: { regulation, sport, league, event_id }.
  // Every call answers that page's lineup view, writes included.
  getSurfaceLineup: async (orgSlug, slug, view = {}, config = {}) => (
    await api.get(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/lineup`, scopedConfig({ timeout: LINEUP_READ_TIMEOUT_MS, ...config }, { params: view }))
  ).data,
  setSurfaceRecipe: async (orgSlug, slug, recipeId, enabled, view = {}, config = {}) => (
    await api.put(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/recipes`, { recipe_id: recipeId, enabled }, scopedConfig(config, { params: view }))
  ).data,
  setSurfacePacks: async (orgSlug, slug, packIds, enabled, view = {}, config = {}) => (
    await api.put(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/packs`, { pack_ids: packIds, enabled }, scopedConfig(config, { params: view }))
  ).data,
  // A pack placed by hand (#1040): inserted at the position, the unpinned
  // packs between shift along around the pins; no pin is written.
  moveSurfacePack: async (orgSlug, slug, prepackId, position, view = {}, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/move`, { prepack_id: prepackId, position }, scopedConfig(config, { params: view }))
  ).data,
  pinSurfacePack: async (orgSlug, slug, prepackId, position, view = {}, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/pins`, { prepack_id: prepackId, position }, scopedConfig(config, { params: view }))
  ).data,
  unpinSurfacePack: async (orgSlug, slug, prepackId, view = {}, config = {}) => (
    await api.delete(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/pins/${encodeURIComponent(prepackId)}`, scopedConfig(config, { params: view }))
  ).data,
  // Without a prepackId the surface's mix chooses the replacement.
  replaceSurfaceSlot: async (orgSlug, slug, position, prepackId = null, view = {}, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/replace`, { position, prepack_id: prepackId }, scopedConfig(config, { params: view }))
  ).data,
  // Show now (#1040): the server chooses the position and the pack that leaves.
  promoteSurfacePack: async (orgSlug, slug, prepackId, view = {}, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/promote`, { prepack_id: prepackId }, scopedConfig(config, { params: view }))
  ).data,
  // payload: { mix, surface_updated_at }; the preview writes nothing.
  previewSurfaceMix: async (orgSlug, slug, payload, view = {}, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/mix/preview`, payload, scopedConfig(config, { params: view }))
  ).data,
  saveSurfaceMix: async (orgSlug, slug, payload, view = {}, config = {}) => (
    await api.put(`/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}/mix`, payload, scopedConfig(config, { params: view }))
  ).data,
  saveSurface: async (orgSlug, slug, payload, config = {}) => {
    const endpoint = slug
      ? `/${orgSlug}/prepacks/surfaces/${encodeURIComponent(slug)}`
      : `/${orgSlug}/prepacks/surfaces`
    const response = slug
      ? await api.put(endpoint, payload, scopedConfig(config))
      : await api.post(endpoint, payload, scopedConfig(config))
    return response.data
  },

  // Automations
  generateTitle: async (orgSlug, payload, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/title`, payload, scopedConfig(config))
  ).data,
  restylePrepack: async (orgSlug, payload, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/style`, payload, scopedConfig(config))
  ).data,
  designTemplate: async (orgSlug, payload, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/templates/design`, payload, scopedConfig(config))
  ).data,
  // An image call for the pack editor and the template builder's scene try-out (#977): the provider takes tens of seconds. The payload's optional template_id (#606) makes the
  // server refuse (403) a template that doesn't allow AI pictures; payload.title and payload.layout carry unsaved composition choices when no template_id is sent.
  generateBanner: async (orgSlug, payload, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/banner`, payload, scopedConfig({ timeout: 180000, ...config }))
  ).data,
  // Schedule-builder and template-builder tracked previews (issue 684/#1009): the POST payload carries { template_id?, fill, layout?, recipe_id?, session? };
  // the answer lists only saved-template previews composed for the template's current title in that fill.
  listBannerPreviews: async (orgSlug, params, config = {}) => (
    await api.get(`/${orgSlug}/prepacks/banner/previews`, scopedConfig(config, { params }))
  ).data,
  // Paints one preview as generateBanner paints a picture and stores it for the schedule (recipe_id) or the builder
  // session: { preview }. Nothing is written into the schedule.
  generateBannerPreview: async (orgSlug, payload, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/banner/previews`, payload, scopedConfig({ timeout: 180000, ...config }))
  ).data,
  // The sample team the template builder previews with (#572): { team }, with
  // logo_url only when the organization may show team logos. `config.team`
  // asks for that catalog team (#602) instead of the default Eagles.
  fetchTemplatePreviewTeam: async (orgSlug, { team, ...config } = {}) => (
    await api.get(`/${orgSlug}/prepacks/templates/preview-team`, scopedConfig(config, team ? { params: { team } } : {}))
  ).data,
  // Every catalog team the builder's team picker offers (#602): { teams: [{ id, name, nickname, league, colors }] },
  // sorted by league, then name.
  fetchTemplateTeams: async (orgSlug, config = {}) => (
    await api.get(`/${orgSlug}/prepacks/templates/teams`, scopedConfig(config))
  ).data,
  runAutomation: async (orgSlug, id, config = {}) => {
    const timeout = config.timeout ?? 120000
    const requestId = config.requestId || config.request_id || config.data?.request_id
    const data = {
      ...(config.data ?? {}),
      ...(requestId ? { request_id: requestId } : {}),
    }
    const response = await api.post(
      `/${orgSlug}/prepacks/automations/${encodeURIComponent(id)}/run`,
      data,
      scopedConfig(config, { timeout }),
    )
    return response.data
  },
  listAutomationRuns: async (orgSlug, params = {}, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/automations/runs`,
      scopedConfig(config, { params }),
    )
    return response.data
  },

  // Configuration
  getConfiguration: async (orgSlug, config = {}) => {
    const response = await api.get(`/${orgSlug}/prepacks/configuration`, scopedConfig(config))
    return response.data
  },
  saveConfiguration: async (orgSlug, payload, config = {}) => {
    const response = await api.put(
      `/${orgSlug}/prepacks/configuration`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },

  // Events (for Generator)
  // Legs related to the builder's current selection (same player, side, or game).
  suggestOutcomes: async (orgSlug, payload, config = {}) => (
    await api.post(`/${orgSlug}/prepacks/outcomes/suggestions`, payload, scopedConfig(config))
  ).data,
  // Which catalog markets the regulation's book offers for one builder sport
  // over the lookahead (`state: 'building'` while the server is still
  // reading games; poll again). `league` is optional and blank values are
  // left out; the answer counts games per league either way. `bet_type`
  // (sgp, parlay, straight) makes the answer judge markets for that bet type.
  getMarketAvailability: async (orgSlug, { regulation, sport, league, lookahead_hours: lookaheadHours, bet_type: betType } = {}, config = {}) => {
    const params = Object.fromEntries(
      Object.entries({ regulation, sport, league, lookahead_hours: lookaheadHours, bet_type: betType })
        .filter(([, value]) => value != null && String(value).trim() !== ''),
    )
    const response = await api.get(`/${orgSlug}/prepacks/markets`, scopedConfig(config, { params }))
    return response.data
  },
  searchParticipants: async (orgSlug, params = {}, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/participants/search`,
      scopedConfig(config, { params }),
    )
    return response.data
  },
  // Teams, or players, a schedule may name in its scope (issue 1015): the
  // sportsbook's league rosters, so a club or player without a game this
  // week is still listed. `params`: kind (teams | players), q, regulation,
  // leagues, teams, excluded_teams, values, limit (lists as csv strings).
  getParticipantCatalog: async (orgSlug, params = {}, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/participants/catalog`,
      scopedConfig(config, { params }),
    )
    return response.data
  },

  // Master list
  setRecipeMasterSet: async (orgSlug, id, masterSet, config = {}) => (
    await api.put(`/${orgSlug}/prepacks/recipes/${encodeURIComponent(id)}/master`, { master_set: masterSet }, scopedConfig(config))
  ).data,
  getMasterList: async (config = {}) => (
    await api.get('/prepacks/master-list', scopedConfig(config))
  ).data,
  syncMasterList: async (organization, config = {}) => (
    await api.post('/prepacks/master-list/sync', organization ? { organization } : {}, scopedConfig({ timeout: 120000, ...config }))
  ).data,
  setOrganizationMasterSets: async (orgSlug, masterSets, config = {}) => (
    await api.put(`/prepacks/master-list/organizations/${encodeURIComponent(orgSlug)}`, { master_sets: masterSets }, scopedConfig({ timeout: 120000, ...config }))
  ).data,

  // Recipes
  listBoosts: async (orgSlug, config = {}) => {
    const response = await api.get(`/${orgSlug}/prepacks/boosts`, scopedConfig(config))
    return response.data
  },
  listRecipes: async (orgSlug, config = {}) => {
    const response = await api.get(`/${orgSlug}/prepacks/recipes`, scopedConfig(config))
    return response.data
  },
  getRecipe: async (orgSlug, id, config = {}) => {
    const response = await api.get(
      `/${orgSlug}/prepacks/recipes/${encodeURIComponent(id)}`,
      scopedConfig(config),
    )
    return response.data
  },
  createRecipe: async (orgSlug, payload, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/recipes`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },
  // One transaction on the server: the recipe and its rule, saved off. A repeat
  // of `request_id` answers the first schedule, `{ recipe, created: false }`.
  createScheduleFromPack: async (orgSlug, payload, config = {}) => {
    const response = await api.post(
      `/${orgSlug}/prepacks/recipes/from-pack`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },
  updateRecipe: async (orgSlug, id, payload, revision, config = {}) => {
    const rev = revision ?? payload?.revision
    const params = rev != null ? { revision: rev } : undefined
    const body = rev != null ? { ...payload, revision: rev } : payload
    const response = await api.put(
      `/${orgSlug}/prepacks/recipes/${encodeURIComponent(id)}`,
      body,
      scopedConfig(config, params ? { params } : {}),
    )
    return response.data
  },
  // Super admins only: moves a recipe between Built-in and My schedules.
  setRecipeBuiltIn: async (orgSlug, id, builtIn, config = {}) => {
    const response = await api.put(
      `/${orgSlug}/prepacks/recipes/${encodeURIComponent(id)}/built-in`,
      { built_in: builtIn },
      scopedConfig(config),
    )
    return response.data
  },
  deleteRecipe: async (orgSlug, id, revision, config = {}) => {
    const params = revision != null ? { revision } : undefined
    await api.delete(
      `/${orgSlug}/prepacks/recipes/${encodeURIComponent(id)}`,
      scopedConfig(config, params ? { params } : {}),
    )
  },

  // The rule a schedule owns. Every saved schedule has one; the editor only ever upserts it.
  saveRecipeSchedule: async (orgSlug, id, payload, config = {}) => {
    const response = await api.put(
      `/${orgSlug}/prepacks/recipes/${encodeURIComponent(id)}/schedule`,
      payload,
      scopedConfig(config),
    )
    return response.data
  },
  // How many packs an unsaved recipe and its schedule would make now, or why none (#761): `{ recipe: { name,
  // description, definition }, schedule }` in, nothing stored. The server decodes the body strictly, so it sends
  // exactly what the builder built. Generation runs, so it gets the run's 120 s budget. `savedRecipeId` names the
  // saved schedule being edited, whose shelf the server then reads as its next run would; it goes as a query
  // parameter, which a server from before it ignores.
  dryRunSchedule: async (orgSlug, payload, config = {}, savedRecipeId = null) => {
    const timeout = config.timeout ?? 120000
    const params = savedRecipeId ? { ...(config.params ?? {}), recipe: savedRecipeId } : config.params
    const response = await api.post(
      `/${orgSlug}/prepacks/recipes/dry-run`,
      payload,
      scopedConfig(config, params ? { timeout, params } : { timeout }),
    )
    return response.data
  },
  runRecipeSchedule: async (orgSlug, id, config = {}) => {
    const timeout = config.timeout ?? 120000
    const requestId = config.requestId || config.request_id || config.data?.request_id
    const data = {
      ...(config.data ?? {}),
      ...(requestId ? { request_id: requestId } : {}),
    }
    const response = await api.post(
      `/${orgSlug}/prepacks/recipes/${encodeURIComponent(id)}/schedule/run`,
      data,
      scopedConfig(config, { timeout }),
    )
    return response.data
  },
}
